import { z } from 'zod';
import { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useAppContext } from 'hooks/context';
import { CCard, CContainedButton } from 'components/atoms';
import { COutlinedInput } from 'components/molecules';
import { useCreateContactFormMutation } from 'services/common';
import { Divider } from '@mui/material';
import {
  REGEX_ALPHANUMERIC,
  REGEX_DIGIT,
  REGEX_INTERNATIONAL_ALPHABETIC,
  REGEX_PHONE,
} from 'utils/constants';
import { SecurityCode, loadCaptchaEngine, validateCaptcha } from 'utils/react-simple-captcha';

export default function Help() {
  const { t } = useTranslation(['translation', 'common']);
  const navigate = useNavigate();
  const {
    dialog: { showDialog },
  } = useAppContext();

  const helpSchema = z.object({
    hospitalId: z.number(),
    name: z
      .string()
      .trim()
      .nonempty(t('schema.required'))
      .regex(REGEX_INTERNATIONAL_ALPHABETIC, t('schema.alphabetic'))
      .min(2, t('schema.min-char', { number: 2 }))
      .max(35, t('schema.max-char', { number: 35 })),
    surname: z
      .string()
      .trim()
      .nonempty(t('schema.required'))
      .regex(REGEX_INTERNATIONAL_ALPHABETIC, t('schema.alphabetic'))
      .min(2, t('schema.min-char', { number: 2 }))
      .max(35, t('schema.max-char', { number: 35 })),
    relatedPetMate: z
      .string()
      .trim()
      .nonempty(t('schema.required'))
      .regex(REGEX_ALPHANUMERIC, t('schema.alphanumeric'))
      .or(z.literal('')),
    protocolNo: z.string(),
    phone: z.string().regex(REGEX_PHONE, t('schema.phone')),
    email: z.string().email(t('schema.email')),
    message: z
      .string()
      .trim()
      .nonempty(t('schema.required'))
      .min(5, t('schema.min-char', { number: 5 })),
    securityCode: z
      .string()
      .trim()
      .nonempty(t('schema.required'))
      .regex(REGEX_DIGIT, t('schema.numeric'))
      .length(5, t('schema.min-number', { number: 5 })),
  });

  type HelpSchema = z.infer<typeof helpSchema>;

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    setError,
  } = useForm<HelpSchema>({
    defaultValues: {
      hospitalId: 8,
      name: '',
      surname: '',
      relatedPetMate: '',
      protocolNo: '',
      phone: '',
      email: '',
      message: '',
      securityCode: '',
    },
    resolver: zodResolver(helpSchema),
    mode: 'all',
  });

  const [create, { isLoading }] = useCreateContactFormMutation();

  const onSubmit: SubmitHandler<HelpSchema> = async (formData) => {
    const { name, phone, surname, relatedPetMate, securityCode, ...restFormData } = formData;

    if (validateCaptcha(securityCode)) {
      const { isSuccess } = await create({
        ...restFormData,
        firstName: name,
        lastName: surname,
        phoneNumber: phone,
        relatedPerson: relatedPetMate,
      }).unwrap();

      if (isSuccess) {
        loadCaptchaEngine(5, '#fff', '#000', 'numbers');

        showDialog({
          type: 'success',
          message: t('form.help.success'),
          closeAfterTimeout: 2000,
          onCloseAfterTimeout: () => navigate('/'),
        });
      }
    } else {
      setError('securityCode', { message: t('schema.security-code') });
      loadCaptchaEngine(5, '#fff', '#000', 'numbers');
    }
  };

  return (
    <CCard className="overflow-y-auto px-2 py-4 md:p-8">
      <div className="mb-8 flex flex-col items-center gap-3 text-center">
        <h1 className="text-headline24 text-secondary-900">{t('form.help.title1')}</h1>
        <h3 className="max-w-[916px] text-body18 text-neutral-600">{t('form.help.sub-title1')}</h3>
        <Divider className="my-2 w-full border-secondary-100" />
        <h2 className="text-headline24 text-secondary-900">{t('form.help.title2')}</h2>
        <h4 className="text-body18 text-neutral-800">{t('form.help.sub-title2')}</h4>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2"
        noValidate
      >
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <COutlinedInput
              {...field}
              required
              id="name"
              label={t('form.name')}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="surname"
          render={({ field }) => (
            <COutlinedInput
              {...field}
              required
              id="surname"
              label={t('form.surname')}
              error={!!errors.surname}
              helperText={errors.surname?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="relatedPetMate"
          render={({ field }) => (
            <COutlinedInput
              {...field}
              id="relatedPetMate"
              label={t('form.help.pet-mate')}
              error={!!errors.relatedPetMate}
              helperText={errors.relatedPetMate?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="protocolNo"
          render={({ field }) => (
            <COutlinedInput
              {...field}
              id="protocolNo"
              label={t('form.help.protocol-no')}
              error={!!errors.protocolNo}
              helperText={errors.protocolNo?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <COutlinedInput
              {...field}
              required
              id="phone"
              label={t('form.phone')}
              error={!!errors.phone}
              helperText={errors.phone?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <COutlinedInput
              {...field}
              required
              id="email"
              label={t('form.email')}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          )}
        />

        <div className="col-span-full">
          <Controller
            control={control}
            name="message"
            render={({ field }) => (
              <COutlinedInput
                {...field}
                multiline
                className="w-full"
                required
                id="message"
                label={t('form.help.problem')}
                error={!!errors.message}
                helperText={errors.message?.message}
              />
            )}
          />
        </div>

        <div className="col-span-full">
          <Controller
            control={control}
            name="securityCode"
            render={({ field }) => {
              const { ref, ...rest } = { ...field };
              return (
                <SecurityCode
                  {...rest}
                  innerRef={ref}
                  error={errors.securityCode?.message}
                  label={t('form.security-code')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;

                    if (value === '' || REGEX_DIGIT.test(value)) {
                      field.onChange(value);
                    }
                  }}
                />
              );
            }}
          />
        </div>

        <div className="col-span-full mt-8 h-[40px] w-full max-w-[200px] place-self-center">
          <CContainedButton fullWidth className="h-full" type="submit" disabled={!isValid || isLoading}>
            {t('form.save')}
          </CContainedButton>
        </div>
      </form>
    </CCard>
  );
}
